import '../../i18n/autoTranslate';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import { Activity as Act } from 'lucide-react';

const PAGE_SIZE = 3;

const cx = (...classes) => classes.filter(Boolean).join(' ');

function getColorClasses(color) {
  switch (color) {
    case 'success':
      return 'text-green-400 bg-green-400/10 border-green-400/20';
    case 'accent':
      return 'text-purple-400 bg-purple-400/10 border-purple-400/20';
    case 'warning':
      return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
    case 'primary':
      return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
    default:
      return 'text-gray-400 bg-gray-400/10 border-gray-400/20';
  }
}

function getSpotlightColor(activity) {
  if (activity?.spotlight) return activity.spotlight;
  switch (activity?.color) {
    case 'success':
      return 'rgba(16, 185, 129, 0.15)';
    case 'accent':
      return 'rgba(139, 92, 246, 0.15)';
    case 'warning':
      return 'rgba(245, 158, 11, 0.15)';
    case 'primary':
      return 'rgba(59, 130, 246, 0.15)';
    default:
      return 'rgba(255, 255, 255, 0.10)';
  }
}

function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  const now = new Date();
  const activityDate = new Date(timestamp);

  if (Number.isNaN(activityDate.getTime())) return '';

  const diff = now - activityDate;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  return 'Just now';
}

const ActivitySkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 3 }).map((_, i) => (
      <div
        key={i}
        className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-4 shadow-[0_14px_50px_rgba(0,0,0,0.30)]"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-white/10 animate-pulse" />
          <div className="flex-1">
            <div className="h-4 w-2/3 rounded bg-white/10 animate-pulse" />
            <div className="mt-2 h-3 w-1/2 rounded bg-white/10 animate-pulse" />
            <div className="mt-3 h-3 w-1/3 rounded bg-white/10 animate-pulse" />
          </div>
          <div className="h-3 w-12 rounded bg-white/10 animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

const ActivityFeedEmpty = ({ onStart }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 text-center shadow-[0_14px_50px_rgba(0,0,0,0.30)]">
    <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl border border-white/10 bg-white/10 text-2xl">
      <span aria-hidden="true"><Act size={20}/></span>
    </div>
    <div className="mt-4 text-sm font-semibold text-white">No activity yet</div>
    <div className="mt-1 text-xs text-white/60">Complete your first interview to see your activity here.</div>
    <button
      type="button"
      onClick={onStart}
      className="mt-5 inline-flex items-center justify-center rounded-xl bg-white px-4 py-2 text-xs font-semibold text-black hover:bg-white/90"
    >
      Start First Interview →
    </button>
  </div>
);

const ActivityFeed = ({ activities = [], loading = false }) => {
  const navigate = useNavigate();

  const safeActivities = useMemo(
    () => (Array.isArray(activities) ? activities : []).filter(Boolean),
    [activities]
  );

  const [page, setPage] = useState(1);

  // Reset pagination when the incoming list changes
  useEffect(() => {
    setPage(1);
  }, [safeActivities.length]);

  const visibleActivities = useMemo(
    () => safeActivities.slice(0, page * PAGE_SIZE),
    [safeActivities, page]
  );

  const hasMore = safeActivities.length > visibleActivities.length;

  const handleViewAll = () => navigate('/past-interviews');
  const handleStartFirst = () => navigate('/interview-setup-configuration');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
        <button
          type="button"
          onClick={handleViewAll}
          className="text-sm text-purple-400 hover:text-purple-300 transition-colors duration-150"
        >
          View All →
        </button>
      </div>

      {loading ? (
        <ActivitySkeleton />
      ) : safeActivities.length === 0 ? (
        <ActivityFeedEmpty onStart={handleStartFirst} />
      ) : (
        <>
          <div className="space-y-3">
            {visibleActivities.map((activity) => (
              <SpotlightCard
                key={activity.id ?? `${activity.type ?? 'activity'}-${String(activity.timestamp ?? '')}`}
                className="relative custom-spotlight-card rounded-2xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_14px_50px_rgba(0,0,0,0.35)]"
                spotlightColor={getSpotlightColor(activity)}
              >
                <div className="relative z-10 rounded-2xl flex items-start gap-3 p-4 hover:bg-white/5 transition-all duration-200 ease-smooth">
                  {/* Icon */}
                  <div
                    className={cx(
                      'flex items-center justify-center w-10 h-10 rounded-lg border',
                      getColorClasses(activity.color)
                    )}
                  >
                    <Icon name={activity.icon || 'Activity'} size={18} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1 min-w-0">
                        <h4 className="text-sm font-medium text-white truncate">{activity.title}</h4>
                        <p className="text-xs text-gray-300 line-clamp-2">{activity.description}</p>
                        {activity.score != null && activity.score !== '' && (
                          <div className="flex items-center space-x-1">
                            <Icon name="Star" size={12} className="text-purple-400" />
                            <span className="text-xs font-medium text-purple-400">
                              {Math.round(Number(activity.score))}% Score
                            </span>
                          </div>
                        )}
                      </div>

                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {formatTimestamp(activity.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              </SpotlightCard>
            ))}
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              className="w-full py-3 text-sm text-gray-300 hover:text-white border border-white/10 rounded-xl hover:border-purple-500/20 hover:bg-white/5 transition-all duration-150"
            >
              Load More Activities
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ActivityFeed;
